import React, { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Edit2,
  Plug,
  Eye,
  EyeOff,
  Power,
  PowerOff,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getIntegrations, createIntegration, updateIntegration, deleteIntegration, getInstances } from '../lib/api';
import { cn, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

const INTEGRATION_TYPES = [
  { value: 'sgp', label: 'SGP', description: 'Sistema de Gestão de Provedores' },
];

export const Integrations = () => {
  const [integrations, setIntegrations] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [showToken, setShowToken] = useState({});
  const [instanceToAdd, setInstanceToAdd] = useState('');
  const [formData, setFormData] = useState({
    type: 'sgp',
    name: '',
    url: '',
    token: '',
    app: '',
    instance_ids: [],
  });

  const fetchInstances = async () => {
    try {
      const res = await getInstances();
      setInstances(res.data || []);
    } catch (error) {
      console.error('Error fetching instances:', error);
      toast.error('Erro ao carregar instâncias');
    }
  };

  const fetchIntegrations = async () => {
    try {
      const response = await getIntegrations();
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
      toast.error('Erro ao carregar integrações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
    fetchInstances();
  }, []);

  const handleOpenDialog = (integration = null) => {
    if (integration) {
      setEditingIntegration(integration);
      const instanceIds =
        integration.instance_ids && Array.isArray(integration.instance_ids) && integration.instance_ids.length > 0
          ? integration.instance_ids
          : (integration.instance_id ? [integration.instance_id] : []);
      setFormData({
        type: integration.type,
        name: integration.name,
        url: integration.url,
        token: integration.token,
        app: integration.app || '',
        instance_ids: instanceIds,
      });
    } else {
      setEditingIntegration(null);
      setFormData({
        type: 'sgp',
        name: '',
        url: '',
        token: '',
        app: '',
        instance_ids: [],
      });
    }
    setInstanceToAdd('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.url || !formData.token || !formData.instance_ids?.length) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      if (editingIntegration) {
        await updateIntegration(editingIntegration.id, formData);
        toast.success('Integração atualizada');
      } else {
        await createIntegration(formData);
        toast.success('Integração criada');
      }
      setDialogOpen(false);
      fetchIntegrations();
    } catch (error) {
      console.error('Error saving integration:', error);
      toast.error('Erro ao salvar integração');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir esta integração?')) return;

    try {
      await deleteIntegration(id);
      toast.success('Integração excluída');
      fetchIntegrations();
    } catch (error) {
      console.error('Error deleting integration:', error);
      toast.error('Erro ao excluir integração');
    }
  };

  const handleToggleActive = async (integration) => {
    try {
      await updateIntegration(integration.id, { active: !integration.active });
      fetchIntegrations();
      toast.success(integration.active ? 'Integração desativada' : 'Integração ativada');
    } catch (error) {
      console.error('Error toggling integration:', error);
      toast.error('Erro ao atualizar integração');
    }
  };

  const getTypeLabel = (type) => {
    const found = INTEGRATION_TYPES.find(t => t.value === type);
    return found ? found.label : type.toUpperCase();
  };

  const getInstanceLabel = (instanceId) => {
    if (!instanceId) return 'Sem instância';
    const inst = instances.find(i => i.id === instanceId);
    return inst?.name || instanceId;
  };

  const getIntegrationInstanceIds = (integration) => {
    if (Array.isArray(integration.instance_ids) && integration.instance_ids.length > 0) {
      return integration.instance_ids;
    }
    if (integration.instance_id) return [integration.instance_id];
    return [];
  };

  const addInstanceToForm = (id) => {
    if (!id) return;
    setFormData(prev => {
      const next = Array.isArray(prev.instance_ids) ? [...prev.instance_ids] : [];
      if (!next.includes(id)) next.push(id);
      return { ...prev, instance_ids: next };
    });
  };

  const removeInstanceFromForm = (id) => {
    setFormData(prev => ({
      ...prev,
      instance_ids: (prev.instance_ids || []).filter(x => x !== id),
    }));
  };

  return (
    <div className="flex-1 overflow-auto bg-slate-50" data-testid="integrations-page">
      <div className="max-w-5xl mx-auto p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Integrações</h1>
            <p className="text-slate-500 mt-0.5 text-sm">
              Configure integrações com sistemas ERP
            </p>
          </div>
          <Button onClick={() => handleOpenDialog()} data-testid="add-integration-btn" className="gap-2">
            <Plus size={16} />
            Nova Integração
          </Button>
        </div>

        {/* Integration Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {integrations.map((integration) => (
            <Card
              key={integration.id}
              className={cn(
                "bg-white border border-slate-200 hover:shadow-md transition-shadow",
                !integration.active && "opacity-60"
              )}
              data-testid={`integration-${integration.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-11 h-11 rounded-xl flex items-center justify-center",
                      integration.active ? "bg-primary/10" : "bg-slate-100"
                    )}>
                      <Plug className={integration.active ? "text-primary" : "text-slate-400"} size={20} />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                        {integration.name}
                        <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">
                          {getTypeLabel(integration.type)}
                        </Badge>
                      </CardTitle>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {integration.active ? (
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                            Ativa
                          </span>
                        ) : 'Inativa'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={integration.active}
                    onCheckedChange={() => handleToggleActive(integration)}
                    data-testid={`toggle-integration-${integration.id}`}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">URL</label>
                  <p className="text-sm font-mono text-slate-600 truncate mt-0.5">{integration.url}</p>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Instância</label>
                  <p className="text-sm text-slate-700 mt-0.5">
                    {getIntegrationInstanceIds(integration).length > 0
                      ? getIntegrationInstanceIds(integration).map(getInstanceLabel).join(', ')
                      : 'Sem instância'}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Token</label>
                  <div className="flex items-center gap-2 mt-0.5">
                    <code className="text-sm font-mono text-slate-600 flex-1 truncate bg-slate-50 px-2 py-1 rounded border border-slate-100">
                      {showToken[integration.id] ? integration.token : '••••••••••••••••'}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowToken({ ...showToken, [integration.id]: !showToken[integration.id] })}
                    >
                      {showToken[integration.id] ? <EyeOff size={13} /> : <Eye size={13} />}
                    </Button>
                  </div>
                </div>

                {integration.app && (
                  <div>
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">App</label>
                    <p className="text-sm text-slate-700 mt-0.5">{integration.app}</p>
                  </div>
                )}

                {/* Footer with timestamp and action buttons */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <span className="text-xs text-slate-400">
                    Atualizado: {formatDateTime(integration.updated_at)}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 bg-white border-slate-200 text-slate-600 hover:bg-slate-50 gap-1.5 px-3"
                      onClick={() => handleOpenDialog(integration)}
                      data-testid={`edit-integration-${integration.id}`}
                    >
                      <Edit2 size={13} />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 bg-white border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 gap-1.5 px-3"
                      onClick={() => handleDelete(integration.id)}
                      data-testid={`delete-integration-${integration.id}`}
                    >
                      <Trash2 size={13} />
                      Excluir
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty state */}
        {integrations.length === 0 && !loading && (
          <Card className="bg-white border border-slate-200">
            <CardContent className="py-16">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Plug className="w-8 h-8 text-slate-300" />
                </div>
                <p className="text-lg font-semibold text-slate-700 mb-1">Nenhuma integração configurada</p>
                <p className="text-sm text-slate-400 mb-6">Clique em "Nova Integração" para conectar seu ERP</p>
                <Button onClick={() => handleOpenDialog()} className="gap-2">
                  <Plus size={16} />
                  Nova Integração
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Available Integrations Info */}
        <Card className="bg-white border border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-slate-800">Integrações Disponíveis</CardTitle>
            <CardDescription className="text-slate-500">
              Sistemas ERP compatíveis com a plataforma
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {INTEGRATION_TYPES.map((type) => (
                <div key={type.value} className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-100 rounded-xl">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Plug className="text-primary" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{type.label}</p>
                    <p className="text-sm text-slate-500">{type.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-800">
                {editingIntegration ? 'Editar Integração' : 'Nova Integração'}
              </DialogTitle>
              <DialogDescription className="text-slate-500">
                Configure os dados de conexão com o sistema ERP
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Instância *</label>
                <Select value={instanceToAdd} onValueChange={(value) => setInstanceToAdd(value)}>
                  <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="integration-instance-select">
                    <SelectValue placeholder="Selecione uma instância" />
                  </SelectTrigger>
                  <SelectContent>
                    {instances.map((inst) => (
                      <SelectItem key={inst.id} value={inst.id}>
                        {inst.name || inst.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      addInstanceToForm(instanceToAdd);
                      setInstanceToAdd('');
                    }}
                    disabled={!instanceToAdd}
                    data-testid="integration-add-instance-btn"
                    className="bg-white"
                  >
                    Adicionar
                  </Button>
                  <span className="text-xs text-slate-400">
                    Selecione uma instância e clique em Adicionar
                  </span>
                </div>

                {formData.instance_ids?.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1" data-testid="integration-instance-chips">
                    {formData.instance_ids.map((id) => (
                      <Badge key={id} variant="outline" className="flex items-center gap-1 bg-primary/5 text-primary border-primary/20">
                        {getInstanceLabel(id)}
                        <button
                          type="button"
                          className="ml-1 inline-flex items-center justify-center hover:text-red-500 transition-colors"
                          onClick={() => removeInstanceFromForm(id)}
                          aria-label="Remover instância"
                        >
                          <X size={12} />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Tipo</label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="integration-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTEGRATION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Nome *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: SGP Produção"
                  className="bg-slate-50 border-slate-200"
                  data-testid="integration-name-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">URL *</label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://api.sgp.com.br"
                  className="bg-slate-50 border-slate-200"
                  data-testid="integration-url-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Token *</label>
                <Input
                  value={formData.token}
                  onChange={(e) => setFormData({ ...formData, token: e.target.value })}
                  placeholder="Token de autenticação"
                  className="bg-slate-50 border-slate-200"
                  data-testid="integration-token-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">App</label>
                <Input
                  value={formData.app}
                  onChange={(e) => setFormData({ ...formData, app: e.target.value })}
                  placeholder="Identificador do app"
                  className="bg-slate-50 border-slate-200"
                  data-testid="integration-app-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="bg-white">
                Cancelar
              </Button>
              <Button onClick={handleSave} data-testid="save-integration-btn">
                {editingIntegration ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Integrations;
